import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUsers, FiChevronRight, FiChevronLeft, FiSearch, FiLoader } from 'react-icons/fi';
import { teamsApi, membersApi } from '../services/api';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';

export default function ManageTeamMembers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedPortal } = useHubSpotPortals();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [allOwners, setAllOwners] = useState([]);
  const [currentMembers, setCurrentMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingMemberAction, setPendingMemberAction] = useState(null);

  const fetchData = useCallback(async (showLoading = true) => {
    if (!id || !selectedPortal?.portal_id) return;

    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Fetch team data and current members
      const teamResponse = await teamsApi.fetchTeamById(id);
      setTeamData(teamResponse);
      setCurrentMembers(teamResponse.members || []);

      // Fetch all HubSpot owners for this portal
      const ownersResponse = await membersApi.fetchHubspotOwners(selectedPortal.portal_id);
      setAllOwners(ownersResponse.owners || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load team member data. Please try again.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [id, selectedPortal?.portal_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate available users (not currently on the team)
  useEffect(() => {
    const currentMemberOwnerIds = new Set(currentMembers.map(member => member.hs_owner_id));
    const available = allOwners.filter(owner => !currentMemberOwnerIds.has(owner.id));
    available.sort((a, b) => {
      const aFirst = (a.firstName || a.first_name || '').toLowerCase();
      const bFirst = (b.firstName || b.first_name || '').toLowerCase();
      if (aFirst && bFirst) return aFirst.localeCompare(bFirst, 'en', { sensitivity: 'base' });
      if (aFirst) return -1;
      if (bFirst) return 1;
      const aLast = (a.lastName || a.last_name || '').toLowerCase();
      const bLast = (b.lastName || b.last_name || '').toLowerCase();
      return aLast.localeCompare(bLast, 'en', { sensitivity: 'base' });
    });
    setAvailableUsers(available);
  }, [allOwners, currentMembers]);

  const handleAddMember = async (owner) => {
    const actionKey = `add:${owner.id}`;
    setPendingMemberAction(actionKey);
    setError(null);

    try {
      const memberData = {
        hs_owner_id: owner.id,
        hs_user_id: owner.userId,
        first_name: owner.firstName,
        last_name: owner.lastName,
        portal_id: selectedPortal.portal_id,
        percent: 100,
        weight: 1
      };

      await membersApi.addMemberToTeam(id, memberData);
      
      // Refresh data to update the lists
      await fetchData(false);
    } catch (err) {
      console.error('Error adding member:', err);
      setError('Failed to add member to team. Please try again.');
    } finally {
      setPendingMemberAction(null);
    }
  };

  const handleRemoveMember = async (member) => {
    const actionKey = `remove:${member.id}`;
    setPendingMemberAction(actionKey);
    setError(null);

    try {
      await membersApi.removeMemberFromTeam(id, member.id);
      
      // Refresh data to update the lists
      await fetchData(false);
    } catch (err) {
      console.error('Error removing member:', err);
      setError('Failed to remove member from team. Please try again.');
    } finally {
      setPendingMemberAction(null);
    }
  };

  const handleBack = () => navigate(`/teams/${id}`);

  const filterUsers = (users, searchTerm) => {
    if (!searchTerm) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(user => 
      (user.firstName && user.firstName.toLowerCase().includes(term)) ||
      (user.lastName && user.lastName.toLowerCase().includes(term)) ||
      (user.email && user.email.toLowerCase().includes(term)) ||
      (user.first_name && user.first_name.toLowerCase().includes(term)) ||
      (user.last_name && user.last_name.toLowerCase().includes(term))
    );
  };

  const filteredAvailableUsers = filterUsers(availableUsers, searchTerm);
  const filteredCurrentMembers = filterUsers(currentMembers, searchTerm);

  const UserCard = ({
    user,
    onAction,
    actionIcon,
    actionLabel,
    actionColor = "blue",
    disabled = false,
    isPending = false,
    pendingLabel,
  }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">
            {user.firstName || user.first_name} {user.lastName || user.last_name}
          </h4>
          <p className="text-sm text-gray-600">{user.email}</p>
          {user.percent && (
            <p className="text-xs text-gray-500 mt-1">Weight: {user.percent}%</p>
          )}
        </div>
        <button
          onClick={() => onAction(user)}
          disabled={disabled}
          className={`ml-4 p-2 bg-${actionColor}-600 hover:bg-${actionColor}-700 text-white rounded-lg transition-colors duration-200 inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
          title={isPending ? pendingLabel : actionLabel}
          aria-label={isPending ? pendingLabel : actionLabel}
          aria-busy={isPending}
        >
          {isPending ? (
            <>
              <FiLoader className="w-4 h-4 animate-spin" aria-hidden="true" />
              <span className="text-sm font-medium">{pendingLabel}</span>
            </>
          ) : actionIcon}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div className="flex items-center">
          <button 
            onClick={handleBack} 
            className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition duration-200"
            title="Back to Team Details"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Team Members</h1>
            <p className="text-gray-600">
              {teamData?.team?.custom_team_name || 'Team'} • Team ID: {id}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button onClick={() => fetchData()} className="mt-2 text-red-600 hover:text-red-700 text-sm underline">
            Retry
          </button>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading team member data...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Available Users */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <FiUsers className="w-5 h-5 text-gray-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Available Users</h2>
                <span className="ml-2 bg-gray-100 text-gray-600 text-sm px-2 py-1 rounded-full">
                  {filteredAvailableUsers.length}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">Click to add to team</p>
            </div>
            <div className="p-6 max-h-96 overflow-y-auto">
              {filteredAvailableUsers.length === 0 ? (
                <div className="text-center py-8">
                  <FiUsers className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">
                    {searchTerm ? 'No matching users found' : 'All users are already on this team'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredAvailableUsers.map((user) => (
                    <UserCard
                      key={user.id}
                      user={user}
                      onAction={handleAddMember}
                      actionIcon={<FiChevronRight className="w-4 h-4" />}
                      actionLabel="Add to team"
                      actionColor="green"
                      disabled={pendingMemberAction !== null}
                      isPending={pendingMemberAction === `add:${user.id}`}
                      pendingLabel="Adding..."
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Current Team Members */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <FiUsers className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
                <span className="ml-2 bg-blue-100 text-blue-600 text-sm px-2 py-1 rounded-full">
                  {filteredCurrentMembers.length}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">Click to remove from team</p>
            </div>
            <div className="p-6 max-h-96 overflow-y-auto">
              {filteredCurrentMembers.length === 0 ? (
                <div className="text-center py-8">
                  <FiUsers className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">
                    {searchTerm ? 'No matching team members found' : 'No team members yet'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredCurrentMembers.map((member) => (
                    <UserCard
                      key={member.id}
                      user={member}
                      onAction={handleRemoveMember}
                      actionIcon={<FiChevronLeft className="w-4 h-4" />}
                      actionLabel="Remove from team"
                      actionColor="red"
                      disabled={pendingMemberAction !== null}
                      isPending={pendingMemberAction === `remove:${member.id}`}
                      pendingLabel="Removing..."
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
